/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { PictoChartElement } from './PictoChartElement';
import type { PictoChartItem, PictoChart } from './PictoChart.type';
import { baseStyles } from './themes/PictoChartStyles.css';
import { classNames } from '../utils/UNSAFE_classNames';
import { TrackResizeContainer } from '../PRIVATE_TrackResizeContainer';
import { Size, sizeToCSS } from '../utils/UNSAFE_size';
import { generateElementCoords, getFlowingInfo } from './PictoChartLayoutUtils';
import { useUser } from '../hooks/UNSAFE_useUser';
import { useSelection } from '../hooks/PRIVATE_useVisSelection';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '../resources/nls/bundle';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { getItemInfo, getDatatipContent, getItemProps, getTotalCount } from './PictoChartUtils';
import { getPictoChartNavUtils } from './PictoChartNavUtils';
import { useEvents } from './useEvents';
import { usePictoChartDatatip } from './usePictoChartDatatip';
import { useItemFocus } from '../hooks/PRIVATE_useVisEvents';
import { PictoChartContext } from './PictoChartContext';
import { useTestId } from '../hooks/UNSAFE_useTestId';

export function PictoChart<
  K extends string | number,
  D extends PictoChartItem<K> = PictoChartItem<K>
>({
  width,
  height,
  columnWidth,
  rowHeight,
  items,
  columnCount,
  rowCount,
  layout,
  layoutOrigin = 'topStart',
  selectionMode = 'none',
  highlightedIds = [],
  hiddenIds = [],
  selectedIds = [],
  onItemAction,
  onItemInput,
  ...props
}: PictoChart<K, D>) {
  //  TODO: investigate if we need to handle empty items for performance reasons.
  const hasData = items.length !== 0;
  const filteredItems = items.filter((i) => !hiddenIds?.includes(i.id));
  const totalCount = getTotalCount(filteredItems);
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const supportsSelection = !!(selectionMode && selectionMode !== 'none');
  const supportsAction = onItemAction != null;
  const isReadOnly = !supportsSelection && !supportsAction && onItemInput === null;
  const { direction } = useUser();
  const testIdProps = useTestId(props.testId);

  const { getItem, getDetailFromInfo, getPrevItemInfo, getNextItemInfo } = getPictoChartNavUtils<
    K,
    D
  >(filteredItems);
  const { hoveredItemInfo, focusedItemInfo, ...eventsProps } = useEvents<K>(
    !isReadOnly && hasData,
    getDetailFromInfo,
    getPrevItemInfo,
    getNextItemInfo,
    onItemAction,
    onItemInput
  );

  const idExtracter = (event: Event) => {
    // return focused item for keyboard event.
    // eventsProps should have already updated the focused item
    const info =
      event.type === 'keyup' ? focusedItemInfo : getItemInfo(event.target as HTMLElement);
    return getDetailFromInfo(info).id;
  };

  const selectionProps = useSelection<K>({
    idExtracter,
    isDrillEnabled: onItemAction != undefined,
    selection: selectedIds,
    selectionMode: selectionMode,
    onChange: props.onSelectionChange
  });

  const rectangleWidth = columnWidth && parseFloat(columnWidth);
  const rectangleHeight = rowHeight && parseFloat(rowHeight);
  const pictoStyleClasses = classNames([baseStyles]);
  const { focusedItemRef } = useItemFocus(focusedItemInfo);
  const { content, borderColor } = getDatatipContent(
    filteredItems,
    getItem,
    totalCount,
    focusedItemInfo,
    hoveredItemInfo,
    props.datatip,
    translations
  );

  const isFlowingWidth = width === undefined;
  const isFlowingHeight = height === undefined;

  return (
    <PictoChartContext.Provider value={{ supportsSelection }}>
      <TrackResizeContainer
        width={isFlowingWidth ? 'initial' : (!isFlowingWidth && sizeToCSS(width as Size)) || '0px'}
        height={
          isFlowingHeight ? 'initial' : (!isFlowingHeight && sizeToCSS(height as Size)) || '0px'
        }>
        {function _(width, height) {
          const { calcColCount, calcRowCount, calcColWidth, calcRowHeight, gridGap } =
            getFlowingInfo<K, D>(
              items,
              layout === 'vertical',
              width,
              height,
              isFlowingWidth,
              isFlowingHeight,
              columnCount,
              rowCount,
              rectangleWidth,
              rectangleHeight
            );

          const { datatipContent, datatipProps } = usePictoChartDatatip({
            content,
            width,
            borderColor,
            focusedItemRef,
            focusedItemInfo
          });

          if (!calcColCount)
            // Nothing to render.
            return null;

          const elements = generateElementCoords(
            filteredItems,
            calcRowCount,
            calcColCount,
            layoutOrigin,
            layout === 'vertical',
            direction === 'rtl'
          );

          const mergedEventProps = mergeProps(eventsProps, datatipProps, selectionProps);
          return (
            <div
              tabIndex={0}
              aria-label={props['aria-label']}
              aria-describedby={props['aria-describedby']}
              aria-labelledby={props['aria-labelledby']}
              role="application"
              style={{
                width: isFlowingWidth ? 'max-content' : `${width}px`,
                height: isFlowingHeight ? 'max-content' : `${height}px`,
                gridTemplateRows: `repeat(${calcRowCount}, ${calcRowHeight}px)`,
                gridTemplateColumns: `repeat(${calcColCount}, ${calcColWidth}px)`,
                gap: `${gridGap}px`
              }}
              class={pictoStyleClasses}
              {...testIdProps}
              {...mergedEventProps}>
              {elements.map((elt) => {
                const isFocusedItem = focusedItemInfo.idx === elt.itemIdx;
                const itemProps = getItemProps(
                  elt.item,
                  elt.itemIdx,
                  elt.countIdx,
                  isReadOnly,
                  hoveredItemInfo,
                  selectedIds,
                  focusedItemInfo,
                  highlightedIds,
                  eventsProps['aria-activedescendant']
                );

                const context = {
                  data: elt.item,
                  state: {
                    selected: itemProps.isSelected,
                    focused: itemProps.isFocused,
                    hovered: itemProps.isCurrent
                  }
                };

                return (
                  <PictoChartElement<K, D>
                    focusedItemRef={isFocusedItem ? focusedItemRef : undefined}
                    row={elt.row}
                    column={elt.col}
                    supportsSelection={supportsSelection}
                    supportsAction={supportsAction}
                    translations={translations}
                    totalCount={totalCount}
                    {...itemProps}>
                    {props.children(context)}
                  </PictoChartElement>
                );
              })}
              {datatipContent}
            </div>
          );
        }}
      </TrackResizeContainer>
    </PictoChartContext.Provider>
  );
}
